import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from '@mui/material';
import {
  Reviews as ReviewsIcon,
  SmartToy as SmartToyIcon,
  SupportAgent as SupportAgentIcon,
} from '@mui/icons-material';

import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { format } from 'date-fns';

import Header from '../../components/Header';
import Nav from '../../components/Nav';
import { Body, PageContainer } from '../../components/Layout';
import Popup from '../../components/Popup';
import { ErrorBoundaryFallback, ErrorBoundaryHandler } from '../../components/ErrorBoundary';

import {
  ButtonsContainer,
  Container,
  DateContainer,
  DateInput,
  StyledButton,
  StyledCalendarTodayIcon,
  StyledDateRange,
  StyledDetailsItem,
  StyledPagination,
  StyledPopup,
  StyledTableContainer,
  StyledTextField,
  Title,
} from './styles';

import StatCounter from '../../components/StatCounter';

import { logMessage } from '../../logger';
import getReviews from '../../api/getReviews';
import postReply from '../../api/postReply';
import getReviewDetails from '../../api/getReviewDetails';
import getStores from '../../api/getStores';
import { useGetReviewTicketDetailQuery } from '../../api/reviewTicketAPI';
import ReviewTicket from '../../containers/ReviewTicket';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onPageChange } = props;
  const handlePagination = (event, value) => {
    logMessage(event);
    onPageChange(event, value - 1);
  };

  return (
    <Box sx={{ display: 'flex', ml: 2.5 }}>
      <StyledPagination
        count={Math.ceil(count / rowsPerPage)}
        siblingCount={0}
        page={page + 1}
        onChange={handlePagination}
      />
    </Box>
  );
}

function formatMilliseconds(ms: number): string {
  return format(new Date(ms), 'd MMM yyyy HH:mm');
}

const getDayRangeInSeconds = (startDate: Date, endDate: Date): { start: string; end: string } => {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);
  const startSeconds = Math.floor(start.getTime() / 1000);
  const endSeconds = Math.floor(end.getTime() / 1000);
  return { start: String(startSeconds), end: String(endSeconds) };
};

const providerLogos: Record<string, string> = {
  shopee: '/pics/shopee_small.png',
  lazada: '/pics/lazada_small.png',
  line: '/pics/line_small.png',
  shopify: '/pics/shopify_small.png',
  whatsapp: '/pics/whatsapp_small.png',
  tiktok: '/pics/tiktok_small.png',
};

const ratingStar: Record<string, string> = {
  '1': '★',
  '2': '★ ★',
  '3': '★ ★ ★',
  '4': '★ ★ ★ ★',
  '5': '★ ★ ★ ★ ★',
};

const Reviews = () => {
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('');
  const [store, setStore] = useState<string[]>(['all']);
  const [rating, setRating] = useState<string[]>(['all']);
  const [commented, setCommented] = useState('');
  const [reply, setReply] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showReplyPopup, setShowReplyPopup] = useState(false);
  const [showReviewDetailsPopup, setShowReviewDetailsPopup] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableSourceData, setTableSourceData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [dataCounter, setDataCounter] = useState<{
    total: number;
    automatedReply: number;
    agentReplied: number;
    sellerCenterReplied: number;
    ticketCreated: number;
  }>({
    total: 0,
    automatedReply: 0,
    agentReplied: 0,
    sellerCenterReplied: 0,
    ticketCreated: 0,
  });
  const [selectedSkuId, setSelectedSkuId] = useState('');
  const [reviewDetails, setReviewDetails] = useState<any>(null);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [selectedReviewTicketId, setSelectedReviewTicketId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [storeList, setStoreList] = useState<
    {
      id: string;
      name: string;
      provider: string;
      countryCode: string;
    }[]
  >([
    {
      id: 'all',
      name: 'All',
      provider: '',
      countryCode: '',
    },
  ]);
  const [date, setDate] = useState(() => {
    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 6);
    return [
      {
        startDate: weekAgo,
        endDate: now,
        key: 'selection',
      },
    ];
  });

  const { data: reviewTicketDetail } = useGetReviewTicketDetailQuery(
    selectedReviewTicketId as string,
    {
      skip: !selectedReviewTicketId,
      refetchOnMountOrArgChange: true,
    },
  );

  const datePickerRef = useRef<HTMLDivElement>(null);
  const hasInitialized = useRef(false);
  const intl = useIntl();
  const storeLabel = intl.formatMessage({ id: 'reviews.store_label', defaultMessage: 'Store' });
  const columns = [
    {
      id: 'store',
      label: 'Store Name',
      render: (row) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={providerLogos[row.store?.provider]}
              alt={row.store?.provider}
              style={{ width: 20, height: 20, marginRight: 5 }}
            />
            {row.store?.name} ({row.store?.countryCode})
          </div>
        );
      },
      sortable: true,
    },
    {
      id: 'sku',
      label: 'SKU',
      render: (row) => row.product?.name ?? '',
      sortable: true,
    },
    {
      id: 'orderId',
      label: 'Order #',
      render: (row) => row.orderId ?? '',
      sortable: true,
    },
    {
      id: 'overallRating',
      label: 'Rating',
      render: (row) => ratingStar[row.overallRating],
      sortable: true,
    },
    {
      id: 'buyerUsername',
      label: 'Customer Name',
      render: (row) => row.buyerUsername ?? '',
      sortable: true,
    },
    {
      id: 'comment',
      label: 'Review Text',
      render: (row) => row.comment ?? '',
      sortable: true,
    },
    {
      id: 'timestamp',
      label: 'Timestamp',
      render: (row: any) => {
        return row.timestamp ? formatMilliseconds(row.timestamp) : '';
      },
      sortable: true,
    },
    {
      id: 'reply',
      label: 'Reply Text',
      render: (row) => (
        <span
          style={{ color: 'dodgerblue', textDecoration: 'underline', cursor: 'pointer' }}
          onClick={() => openReviewDetails(row)}
        >
          {row.reply?.content ?? ''}
        </span>
      ),
      sortable: true,
    },
    {
      id: 'actions',
      label: 'Actions',
      render: (row) => (
        <ButtonGroup key={`actions-${row._id.$oid}`} aria-label="reviews actions" size="small">
          <Button
            key={`reply-${row._id.$oid}`}
            aria-label="reply review"
            variant="contained"
            color="primary"
            size="small"
            disabled={!row.canReply}
            onClick={() => openReplyPopup(row._id.$oid)}
          >
            <FormattedMessage id="reviews.reply_label" defaultMessage="Reply" />
          </Button>
          {row?.ticketId ? (
            <Button
              key={`view-ticket-${row._id.$oid}`}
              aria-label="view review ticket"
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => openReviewTicketDetail(row)}
            >
              <FormattedMessage id="reviews.view_ticket_label" defaultMessage="View Ticket" />
            </Button>
          ) : (
            <Button
              key={`ticket-${row._id.$oid}`}
              aria-label="create review ticket"
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => openReviewTicket(row)}
            >
              <FormattedMessage id="reviews.create_ticket_label" defaultMessage="Ticket" />
            </Button>
          )}
        </ButtonGroup>
      ),
      sortable: false,
    },
  ];

  const handleReviewListResponse = (response: any) => {
    if (response?.data && response?.meta?.pagination) {
      setTableSourceData(response.data);
      setTotalCount(response.meta.pagination.total);
    }

    if (response?.meta?.counter) {
      setDataCounter({
        total: response.meta.counter.total ?? 0,
        automatedReply: response.meta.counter.automatedReply ?? 0,
        agentReplied: response.meta.counter.agentReplied ?? 0,
        sellerCenterReplied: response.meta.counter.sellerCenterReplied ?? 0,
        ticketCreated: response.meta.counter.ticketCreated ?? 0,
      });
    }
  };

  const openReplyPopup = (id: string) => {
    receiveReviewDetails(id);
    setSelectedSkuId(id);
    setReplyText('');
    setShowReplyPopup(true);
  };

  const openReviewDetails = (row: any) => {
    receiveReviewDetails(row._id.$oid);
    setShowReviewDetailsPopup(true);
  };

  const openReviewTicket = (row: any) => {
    setSelectedReviewId(row._id.$oid);
    receiveReviewDetails(row._id.$oid);
    setShowTicketForm(true);
  };

  const openReviewTicketDetail = (row: any) => {
    setSelectedReviewId(row._id.$oid);
    setSelectedReviewTicketId(row?.ticketId);
    receiveReviewDetails(row._id.$oid);
    setShowTicketDetail(true);
  };

  const fetchReviews = (currentPage: number = page, currentRowsPerPage: number = rowsPerPage) => {
    getReviews(
      {
        page: currentPage + 1,
        perPage: currentRowsPerPage,
        provider: platform,
        rating: rating.includes('all') ? '' : rating.join(','),
        query: search,
        startDate: startDate,
        endDate: endDate,
        commented: commented,
        reply: reply,
        store: store.includes('all') ? '' : store.join(','),
      },
      handleReviewListResponse,
    );
  };

  const handleChangePage = (event: any, newPage: number) => {
    logMessage(event);
    setPage(newPage);
    fetchReviews(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    logMessage(event);
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
    fetchReviews(0, newRows);
  };

  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnId);
      setSortOrder('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortBy) return tableSourceData;
    return [...tableSourceData].sort((a, b) => {
      const valA = a[sortBy];
      const valB = b[sortBy];
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tableSourceData, sortBy, sortOrder]);
  const handleApplyFilters = () => {
    setPage(0);
    fetchReviews(0, rowsPerPage);
  };

  const handleReplySubmit = (e) => {
    e.preventDefault();
    setShowReplyPopup(false);
    postReply(
      selectedSkuId,
      {
        comment: replyText,
      },
      (response: any) => {
        if (response) {
          fetchReviews(page, rowsPerPage);
        }
      },
      (error) => {
        console.log('postReply error', error);
        setShowErrorPopup(true);
      },
    );
    setSelectedSkuId('');
  };

  const receiveReviewDetails = (id: string) => {
    getReviewDetails(
      id,
      (response: any) => {
        setReviewDetails(response);
      },
      (error) => {
        console.log('getReviewDetails error', error);
        setShowErrorPopup(true);
      },
    );
  };

  const handleDateRangeChange = (range) => {
    setDate([range.selection]);
    const { start, end } = getDayRangeInSeconds(range.selection.startDate, range.selection.endDate);
    setStartDate(start);
    setEndDate(end);
  };

  const fetchStores = (data: any[]) => {
    const formattedStores = data.map((store: any) => ({
      id: store._id.$oid,
      name: store.name,
      provider: store.provider,
      countryCode: store.countryCode,
    }));

    formattedStores.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    setStoreList([{ id: 'all', name: 'All', provider: '', countryCode: '' }, ...formattedStores]);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    getStores(fetchStores);
    const { start, end } = getDayRangeInSeconds(date[0].startDate, date[0].endDate);
    setStartDate(start);
    setEndDate(end);
    getReviews(
      {
        page: 1,
        perPage: rowsPerPage,
        provider: platform,
        rating: rating.includes('all') ? '' : rating.join(','),
        query: search,
        startDate: String(start),
        endDate: String(end),
        commented: commented,
        reply: reply,
        store: store.includes('all') ? '' : store.join(','),
      },
      handleReviewListResponse,
    );
  }, [commented, date, platform, rating, reply, rowsPerPage, search, store]);

  return (
    <>
      <ErrorBoundary FallbackComponent={ErrorBoundaryFallback} onError={ErrorBoundaryHandler}>
        <Header />
      </ErrorBoundary>
      <PageContainer>
        <ErrorBoundary FallbackComponent={ErrorBoundaryFallback} onError={ErrorBoundaryHandler}>
          <Nav currentStep="reviews" subStep={'list'} />
        </ErrorBoundary>
        <Body>
          <ErrorBoundary FallbackComponent={ErrorBoundaryFallback} onError={ErrorBoundaryHandler}>
            <Container>
              <Title>
                <FormattedMessage id="reviews.page_title" defaultMessage="Reviews" />
              </Title>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <TextField
                  label={
                    <FormattedMessage
                      id="reviews.search_label"
                      defaultMessage="Search (order #, product, username...)"
                    />
                  }
                  variant="outlined"
                  size="medium"
                  sx={{ minWidth: 270 }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                <FormControl size="medium" sx={{ minWidth: 120 }}>
                  <InputLabel>
                    <FormattedMessage id="reviews.platform_label" defaultMessage="Platform" />
                  </InputLabel>
                  <Select
                    label={
                      <FormattedMessage id="reviews.platform_label" defaultMessage="Platform" />
                    }
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value)}
                  >
                    <MenuItem value="">All Platforms</MenuItem>
                    <MenuItem value="shopee">Shopee</MenuItem>
                    <MenuItem value="lazada">Lazada</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="medium" sx={{ minWidth: 120 }}>
                  <InputLabel>{storeLabel}</InputLabel>
                  <Select
                    multiple
                    value={store}
                    label={storeLabel}
                    onChange={(e) => {
                      const value = e.target.value as string[];

                      if (value.length > 1 && value[value.length - 1] === 'all') {
                        setStore(['all']);
                      } else if (value.length > 1 && value[0] === 'all') {
                        setStore(value.filter((v) => v !== 'all'));
                      } else {
                        setStore(value);
                      }
                    }}
                    renderValue={(selected) => {
                      if (selected.includes('all') || !selected.length) return 'All';

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '6px',
                            alignItems: 'center',
                          }}
                        >
                          {storeList
                            .filter((store) => selected.includes(store.id))
                            .map((store) => (
                              <Box key={store.id} sx={{ display: 'flex', alignItems: 'center' }}>
                                <img
                                  src={providerLogos[store.provider]}
                                  alt={store.provider}
                                  style={{ width: 16, height: 16, marginRight: 4 }}
                                />
                                {store.name} ({store.countryCode})
                              </Box>
                            ))}
                        </Box>
                      );
                    }}
                  >
                    {storeList.map((store) => (
                      <MenuItem key={store.id} value={store.id}>
                        {store.id === 'all' ? (
                          'All'
                        ) : (
                          <>
                            <img
                              src={providerLogos[store.provider]}
                              alt={store.provider}
                              style={{ width: 20, height: 20, marginRight: 8 }}
                            />
                            {store.name} ({store.countryCode})
                          </>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="medium" sx={{ minWidth: 120 }}>
                  <InputLabel>
                    <FormattedMessage id="reviews.reply_label" defaultMessage="Reply" />
                  </InputLabel>
                  <Select
                    label={<FormattedMessage id="reviews.reply_label" defaultMessage="Reply" />}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="0">Unreplied</MenuItem>
                    <MenuItem value="1">Replied</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="medium" sx={{ minWidth: 120 }}>
                  <InputLabel>
                    <FormattedMessage id="reviews.comment_label" defaultMessage="Comment" />
                  </InputLabel>
                  <Select
                    label={<FormattedMessage id="reviews.comment_label" defaultMessage="Comment" />}
                    value={commented}
                    onChange={(e) => setCommented(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="0">No Comment</MenuItem>
                    <MenuItem value="1">Has Comment</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="medium" sx={{ minWidth: 120 }}>
                  <InputLabel>
                    <FormattedMessage id="reviews.rating_label" defaultMessage="Rating" />
                  </InputLabel>
                  <Select
                    label={<FormattedMessage id="reviews.rating_label" defaultMessage="Rating" />}
                    multiple={true}
                    value={rating}
                    onChange={(e) => {
                      const value = e.target.value as string[];

                      if (value.length > 1 && value[value.length - 1] === 'all') {
                        setRating(['all']);
                      } else if (value.length > 1 && value[0] === 'all') {
                        setRating(value.filter((v) => v !== 'all'));
                      } else {
                        setRating(value);
                      }
                    }}
                    renderValue={(selected) => {
                      if (selected.includes('all')) return 'All';
                      return selected.join(', ');
                    }}
                  >
                    <MenuItem key="rating-all" value="all">
                      All
                    </MenuItem>
                    <MenuItem key="rating-1" value="1">
                      {ratingStar['1']}
                    </MenuItem>
                    <MenuItem key="rating-2" value="2">
                      {ratingStar['2']}
                    </MenuItem>
                    <MenuItem key="rating-3" value="3">
                      {ratingStar['3']}
                    </MenuItem>
                    <MenuItem key="rating-4" value="4">
                      {ratingStar['4']}
                    </MenuItem>
                    <MenuItem key="rating-5" value="5">
                      {ratingStar['5']}
                    </MenuItem>
                  </Select>
                </FormControl>
                <DateContainer ref={datePickerRef}>
                  <DateInput onClick={() => setShowCalendar(!showCalendar)}>
                    <StyledCalendarTodayIcon />
                    {format(date[0].startDate, 'd MMM yyyy')} -{' '}
                    {format(date[0].endDate, 'd MMM yyyy')}
                  </DateInput>
                  {showCalendar && (
                    <StyledDateRange
                      editableDateInputs={true}
                      onChange={handleDateRangeChange}
                      moveRangeOnFirstSelection={false}
                      ranges={date}
                      showDateDisplay={false}
                    />
                  )}
                </DateContainer>
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  onClick={handleApplyFilters}
                >
                  <FormattedMessage id="reviews.apply_filters" defaultMessage="Apply Filters" />
                </Button>
              </Box>
              <StatCounter
                items={[
                  {
                    description: intl.formatMessage({ id: 'reviews.counter.total' }),
                    value: dataCounter.total,
                    icon: ReviewsIcon,
                    fg: '#fff',
                  },
                  {
                    description: intl.formatMessage({ id: 'reviews.counter.automatedReply' }),
                    value: dataCounter.automatedReply,
                    icon: SmartToyIcon,
                    fg: '#fff',
                  },
                  {
                    description: intl.formatMessage({ id: 'reviews.counter.agentReplied' }),
                    value: dataCounter.agentReplied,
                    icon: SupportAgentIcon,
                    fg: '#fff',
                  },
                  {
                    description: intl.formatMessage({ id: 'reviews.counter.sellerCenterReplied' }),
                    value: dataCounter.sellerCenterReplied,
                    icon: SupportAgentIcon,
                    fg: '#fff',
                  },
                  {
                    description: intl.formatMessage({ id: 'reviews.counter.ticketCreated' }),
                    value: dataCounter.ticketCreated,
                    icon: ReviewsIcon,
                    fg: '#fff',
                  },
                ]}
              />
              <StyledTableContainer>
                <Table aria-label="reviews table">
                  <TableHead>
                    <TableRow>
                      {columns.map((col) => (
                        <TableCell key={col.id} align={'center'}>
                          {col.sortable ? (
                            <TableSortLabel
                              active={sortBy === col.id}
                              direction={sortOrder}
                              onClick={() => handleSort(col.id)}
                            >
                              {col.label}
                            </TableSortLabel>
                          ) : (
                            col.label
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedData.map((row: any, index: number) => (
                      <TableRow key={`row-${index}`}>
                        {columns.map((col) => (
                          <TableCell key={col.id} align="left">
                            {col.render(row)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                        colSpan={columns.length}
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        SelectProps={{
                          inputProps: {
                            'aria-label': 'rows per page',
                          },
                          native: true,
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        ActionsComponent={TablePaginationActions}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </StyledTableContainer>
              {showReplyPopup && (
                <Popup
                  isPreviewTicket={false}
                  setIsPreviewTicket={() => {}}
                  handleClose={() => setShowReplyPopup(false)}
                >
                  <>
                    <Title>
                      <FormattedMessage id="reviews.title" defaultMessage="Write your reply" />
                    </Title>
                    <StyledDetailsItem>
                      <strong>
                        <FormattedMessage
                          id="reviews.store_details"
                          defaultMessage="Store / Platform / Country: "
                        />
                      </strong>
                      {reviewDetails?.data?.store?.name} / {reviewDetails?.data?.store?.provider} /{' '}
                      {reviewDetails?.data?.store?.countryCode}
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <strong>
                        <FormattedMessage id="reviews.comment_details" defaultMessage="Comment: " />
                      </strong>{' '}
                      {reviewDetails?.data?.comment}
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <strong>
                        <FormattedMessage id="reviews.rating_details" defaultMessage="Rating: " />
                      </strong>{' '}
                      {ratingStar[reviewDetails?.data?.overallRating]}
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <strong>
                        <FormattedMessage
                          id="reviews.product_name_details"
                          defaultMessage="Product name: "
                        />
                      </strong>{' '}
                      {reviewDetails?.data?.product?.name}
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <strong>
                        <FormattedMessage id="reviews.order_details" defaultMessage="Order: " />
                      </strong>{' '}
                      {reviewDetails?.data?.orderId}
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <strong>
                        <FormattedMessage id="reviews.images_details" defaultMessage="Images: " />
                      </strong>
                      {reviewDetails?.data?.images?.length ? (
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                          {reviewDetails.data.images.map((link: string, idx: number) => (
                            <a key={idx} href={link} target="_blank" rel="noreferrer">
                              <img
                                src={link}
                                alt={`Review ${idx + 1}`}
                                style={{
                                  width: '200px',
                                  height: '200px',
                                  objectFit: 'cover',
                                  borderRadius: '5px',
                                  cursor: 'pointer',
                                }}
                              />
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span> None</span>
                      )}
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <strong>
                        <FormattedMessage id="reviews.videos_details" defaultMessage="Videos: " />
                      </strong>
                      {reviewDetails?.data?.videos?.length ? (
                        reviewDetails?.data.videos.map((link: string, idx: number) => (
                          <div key={idx}>
                            <a href={link} target="_blank" rel="noreferrer">
                              {link}
                            </a>
                          </div>
                        ))
                      ) : (
                        <span> None</span>
                      )}
                    </StyledDetailsItem>
                    <form autoComplete="off" onSubmit={handleReplySubmit}>
                      <StyledTextField
                        label="Reply text"
                        variant="outlined"
                        multiline
                        rows={5}
                        value={replyText}
                        helperText="Reply text must be less than 500 characters"
                        onChange={(e) => {
                          if (e.target.value.length <= 500) {
                            setReplyText(e.target.value);
                          }
                        }}
                      />
                      <ButtonsContainer>
                        <StyledButton variant="contained" color="primary" type="submit">
                          <FormattedMessage id="reviews.send_button" defaultMessage="Send" />
                        </StyledButton>
                      </ButtonsContainer>
                    </form>
                  </>
                </Popup>
              )}
              {showReviewDetailsPopup && (
                <Popup
                  isPreviewTicket={false}
                  setIsPreviewTicket={() => {}}
                  handleClose={() => setShowReviewDetailsPopup(false)}
                >
                  <>
                    <Title>
                      <FormattedMessage
                        id="reviews.details_title"
                        defaultMessage="Review details"
                      />
                    </Title>
                    <StyledDetailsItem>
                      <strong>
                        <FormattedMessage
                          id="reviews.store_details"
                          defaultMessage="Store / Platform / Country: "
                        />
                      </strong>
                      {reviewDetails?.data?.store?.name} / {reviewDetails?.data?.store?.provider} /{' '}
                      {reviewDetails?.data?.store?.countryCode}
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <strong>
                        <FormattedMessage id="reviews.comment_details" defaultMessage="Comment: " />
                      </strong>{' '}
                      {reviewDetails?.data?.comment}
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <strong>
                        <FormattedMessage id="reviews.rating_details" defaultMessage="Rating: " />
                      </strong>{' '}
                      {ratingStar[reviewDetails?.data?.overallRating]}
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <strong>
                        <FormattedMessage id="reviews.reply_details" defaultMessage="Reply: " />
                      </strong>{' '}
                      {reviewDetails?.data?.reply?.content}
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <strong>
                        <FormattedMessage id="reviews.images_details" defaultMessage="Images: " />
                      </strong>
                      {reviewDetails?.data?.images?.length ? (
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                          {reviewDetails.data.images.map((link: string, idx: number) => (
                            <a key={idx} href={link} target="_blank" rel="noreferrer">
                              <img
                                src={link}
                                alt={`Review ${idx + 1}`}
                                style={{
                                  width: '200px',
                                  height: '200px',
                                  objectFit: 'cover',
                                  borderRadius: '5px',
                                  cursor: 'pointer',
                                }}
                              />
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span> None</span>
                      )}
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <strong>
                        <FormattedMessage id="reviews.videos_details" defaultMessage="Videos: " />
                      </strong>
                      {reviewDetails?.data?.videos?.length ? (
                        reviewDetails?.data.videos.map((link: string, idx: number) => (
                          <div key={idx}>
                            <a href={link} target="_blank" rel="noreferrer">
                              {link}
                            </a>
                          </div>
                        ))
                      ) : (
                        <span> None</span>
                      )}
                    </StyledDetailsItem>
                  </>
                </Popup>
              )}
              {showErrorPopup && (
                <Popup
                  isPreviewTicket={false}
                  setIsPreviewTicket={() => {}}
                  handleClose={() => setShowErrorPopup(false)}
                >
                  <>
                    <Title>
                      <FormattedMessage
                        id="storeDetails.tabs.tab.title.error"
                        defaultMessage="Error: "
                      />
                    </Title>
                    <div>
                      <strong>
                        <FormattedMessage
                          id="reviews.error_text"
                          defaultMessage="Looks like this review had already been replied. Please check the Seller Center."
                        />
                      </strong>
                    </div>
                  </>
                </Popup>
              )}
              {showTicketForm && selectedReviewId && (
                <StyledPopup
                  isPreviewTicket={false}
                  setIsPreviewTicket={() => {}}
                  handleClose={() => {
                    setShowTicketForm(false);
                    setSelectedReviewId(null);
                  }}
                >
                  <ReviewTicket
                    onClose={(ticketId?: string) => {
                      const id = selectedReviewId;
                      setShowTicketForm(false);
                      setSelectedReviewId(null);
                      if (!id) return;
                      setTableSourceData((prev) =>
                        prev.map((row) =>
                          row?._id?.$oid === id
                            ? { ...row, ticketId: ticketId ?? row.ticketId }
                            : row,
                        ),
                      );
                      if (!ticketId) fetchReviews();
                    }}
                    isPreview={false}
                    setIsPreview={() => {}}
                    isPreviewTicket={false}
                    content={null}
                    review={reviewDetails?.data}
                    customer={reviewDetails?.data?.customer}
                  />
                </StyledPopup>
              )}
              {showTicketDetail && selectedReviewId && (
                <StyledPopup
                  isPreviewTicket={false}
                  setIsPreviewTicket={() => {}}
                  handleClose={() => {
                    setShowTicketDetail(false);
                    setSelectedReviewId(null);
                    setSelectedReviewTicketId(null);
                  }}
                >
                  <ReviewTicket
                    onClose={() => {
                      setShowTicketDetail(false);
                      setSelectedReviewId(null);
                      setSelectedReviewTicketId(null);
                    }}
                    isPreview={true}
                    setIsPreview={() => {}}
                    isPreviewTicket={true}
                    content={reviewTicketDetail?.data?.report}
                    review={reviewDetails?.data}
                    customer={reviewDetails?.data?.customer}
                  />
                </StyledPopup>
              )}
            </Container>
          </ErrorBoundary>
        </Body>
      </PageContainer>
    </>
  );
};
export default Reviews;
